/**
 * @file        src/components/promo/PromoModal.tsx
 * @description The controlled offer window: photograph, one line of offer copy, the shared lead
 *              form and the link to the terms. Wide editorial layout — two columns on desktop, a
 *              bottom-anchored panel on mobile — built on the project's shared Radix Dialog.
 * @universal   knowledge/universals/components.md#promomodal
 * @usage <PromoModal open={isOpen} onOpenChange={setIsOpen} />
 *
 * @rule        NOT A VARIANT OF #LeadFormModal, and it must never become one. That component is the
 *              small centred lead dialog with two consumers; this is a wide editorial window with a
 *              photograph and a terms link. One component serving both would need a mode
 *              flag and two layouts. They correctly share the underlying Dialog — that is where the
 *              sharing belongs.
 * @rule        NO COPY STRING IS TYPED HERE. Every visible word comes from promoConfig.texts. A
 *              string typed at this call site is a second source of truth that will drift from the
 *              rules page — and the rules page is the legal document.
 * @rule        ONE LINE OF COPY (owner decision 2026-10-20, ADR N-PROMO-PERMANENT-1). The window
 *              says the offer in one sentence; every condition lives on the terms page behind the
 *              link. Do not move conditions, lists or counters back into the window.
 */

"use client";

import Link from "next/link";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import LeadForm from "@/components/LeadForm";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { PROMO_PROGRAM } from "@/lib/promo/promoConfig";

// ─── CONSTANTS ─────────────────────────────────────────────────────────

/**
 * Real pixel width of the committed master, reported by series step 12.
 * Read from knowledge, not guessed: a wrong value makes buildPictureSources describe the srcset
 * incorrectly, and 0 makes it return null so every visitor silently drops to the bare 2048-class
 * master instead of a 640-wide AVIF.
 */
const PHOTO_INTRINSIC_WIDTH = 1672;

/**
 * Honest slot widths. At md and above the dialog is capped at max-w-4xl (896px) and split into two
 * columns, so the photograph occupies 448px. Below md it spans the full viewport.
 */
const PHOTO_SIZES = "(min-width: 768px) 448px, 100vw";

// ─── COMPONENT ─────────────────────────────────────────────────────────

interface PromoModalProps {
  /** Whether the window is open. CONTROLLED — this component holds no open state. */
  open: boolean;
  /** Called with the next open state. Radix calls it for Escape, overlay click and the close cross. */
  onOpenChange: (open: boolean) => void;
}

/**
 * The promo offer window.
 *
 * @param props see {@link PromoModalProps}
 * @returns the dialog
 */
export default function PromoModal({ open, onOpenChange }: PromoModalProps) {
  const { texts, rulesHref, image, sourceLabel } = PROMO_PROGRAM;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Bottom sheet below md, centred dialog at md and above. Position lives in FOUR separate
        twMerge groups, so each has to be set and then restored:
          top       top-[50%]        -> top-auto        -> md:top-[50%]
          bottom    (none)           -> bottom-0        -> md:bottom-auto
          translate translate-y-[-50%] -> translate-y-0 -> md:translate-y-[-50%]
        Radius needs `sm:rounded-none` first: DialogContent's own `sm:rounded-lg` is the
        all-corners group, which does NOT conflict with `rounded-t-*`/`rounded-b-*` in twMerge and
        would otherwise survive and round the sheet's bottom corners in the 640-767px band.
        Verified by running the real twMerge, not assumed.
      */}
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[90vh] max-w-4xl gap-0 overflow-y-auto bg-white p-0 top-auto bottom-0 translate-y-0 rounded-t-2xl rounded-b-none sm:rounded-none sm:rounded-t-2xl sm:rounded-b-none md:top-[50%] md:bottom-auto md:translate-y-[-50%] md:grid-cols-2 md:rounded-t-2xl md:rounded-b-2xl"
      >
        {/*
          @rule FOCAL POINT — do not change without redoing the arithmetic.
          Master is 1672 x 941. The significant content is BOTH mats: x 340..900 px
          (20.3%..53.8% of the frame), y 370..850 px.
          `object-position` does NOT pick the point that lands in the centre of the frame — it
          distributes the OVERFLOW of the image over the frame: at p percent, p percent of the
          overflow stays to the left of the visible window and the rest to the right. So the right
          value depends on how much of the photo does not fit, not only on where the subject is.
          For the window's ~4:5 columns the horizontal overflow is 919 px of 1672, which makes the
          centring value 26% — NOT 37% (the subject's own centre) and certainly not 43%.
          Valid horizontal range for ~4:5 frames: 16%..37%. Outside it a mat is clipped — 43%
          cut 55 px off the left edge of the rear-row mat.
          The vertical 73% currently changes nothing (100% of the height is visible on desktop and
          tablet, 98.8% on mobile). It is kept deliberately: if a frame ever becomes narrower in
          aspect, biasing toward the floor rather than the ceiling of the cabin is the correct
          default. CHANGING THE WINDOW'S LAYOUT MEANS RECOMPUTING THE RANGE.
        */}
        <div className="relative h-48 w-full md:h-auto md:min-h-[560px]">
          <ResponsiveImage
            masterUrl={image.src}
            intrinsicWidth={PHOTO_INTRINSIC_WIDTH}
            alt={image.alt}
            sizes={PHOTO_SIZES}
            fill
            className="object-cover [object-position:26%_73%]"
          />
        </div>

        <div className="flex flex-col gap-4 p-6 md:p-8">
          <DialogTitle className="font-heading text-2xl font-normal leading-snug tracking-normal text-text">
            {texts.heading}
          </DialogTitle>

          <LeadForm source={sourceLabel} ctaLabel={texts.ctaLabel} compact />

          {/* @rule THIS IS THE ONLY LINK TO THE RULES PAGE, AND IT IS A LEGAL REQUIREMENT.
              Article 9 of federal law 38-FZ requires the terms to be reachable from the
              advertisement, and since 2026-10-20 every condition of the offer lives on that page
              and nowhere in the window. Removing this link removes the terms from the offer.
              See ADR N-PROMO-PERMANENT-1. */}
          <Link
            href={rulesHref}
            className="w-fit text-sm text-primary underline underline-offset-2"
          >
            {texts.rulesLinkLabel}
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
